import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const LINE_1 = 'The journey of Being that Engineer,';
const LINE_2 = 'start here.';
const FULL = LINE_1 + LINE_2;

const LOGO_SRC = '/icon-512x512.png';
const LOGO_ZOOM = 1.15; // raise (e.g. 1.3) if you want the logo even bigger inside the card

// Put your sound file(s) in /public/sounds/ and list them here.
// Empty (or failing to load) falls back to a synthesized click.
const TYPING_SOUNDS: string[] = [
  // '/sounds/key-1.mp3',
  // '/sounds/key-2.mp3',
];

const COLORS = {
  bg: '#F6F1E7',
  text: '#D9732F',
  accent: '#E88A4A',
  logoGradient: 'linear-gradient(150deg, #FF9242 0%, #FF6F0F 100%)',
  curtainBack: '#F8CFA6', // soft peach layer that follows the orange curtain
};

/* ---------- Sound ---------- */

const createNoise = (ctx: AudioContext) => {
  const length = Math.floor(ctx.sampleRate * 0.05);
  const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < length; i += 1) {
    data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / length, 3);
  }
  return buffer;
};

const playSynth = (ctx: AudioContext, noise: AudioBuffer) => {
  const t = ctx.currentTime;
  const src = ctx.createBufferSource();
  src.buffer = noise;
  src.playbackRate.value = 0.9 + Math.random() * 0.3;
  const filter = ctx.createBiquadFilter();
  filter.type = 'bandpass';
  filter.frequency.value = 1800 + Math.random() * 1800;
  filter.Q.value = 0.9;
  const g = ctx.createGain();
  g.gain.value = 0.3;
  src.connect(filter).connect(g).connect(ctx.destination);
  src.start(t);

  const osc = ctx.createOscillator();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(180 + Math.random() * 40, t);
  osc.frequency.exponentialRampToValueAtTime(70, t + 0.05);
  const thump = ctx.createGain();
  thump.gain.setValueAtTime(0.09, t);
  thump.gain.exponentialRampToValueAtTime(0.0001, t + 0.06);
  osc.connect(thump).connect(ctx.destination);
  osc.start(t);
  osc.stop(t + 0.07);
};

const playSample = (ctx: AudioContext, buffer: AudioBuffer) => {
  const src = ctx.createBufferSource();
  src.buffer = buffer;
  src.playbackRate.value = 0.94 + Math.random() * 0.14;
  const g = ctx.createGain();
  g.gain.value = 0.55 + Math.random() * 0.3;
  src.connect(g).connect(ctx.destination);
  src.start();
};

/* ---------- The "Vuuup" curtain ----------
   Created outside React on purpose: it must survive the route change
   and keep covering the screen while the login page mounts underneath. */

const runCurtain = () => {
  const make = (background: string, z: number) => {
    const el = document.createElement('div');
    Object.assign(el.style, {
      position: 'fixed',
      left: '0',
      top: '0',
      width: '100%',
      height: '125vh',
      background,
      zIndex: String(z),
      pointerEvents: 'none',
      borderRadius: '0 0 50% 50% / 0 0 16vh 16vh', // curved leading edge
      willChange: 'transform',
    });
    document.body.appendChild(el);
    return el;
  };

  const back = make(COLORS.curtainBack, 99998);
  const front = make(COLORS.logoGradient, 99999);
  const frames = [{ transform: 'translateY(0)' }, { transform: 'translateY(-102%)' }];

  const go = (el: HTMLElement, delay: number) => {
    const anim = el.animate(frames, {
      duration: 1150,
      delay,
      easing: 'cubic-bezier(.76,0,.2,1)',
      fill: 'forwards',
    });
    anim.onfinish = () => el.remove();
  };

  go(front, 220); // brief hold so the login page can paint underneath
  go(back, 360);
  window.setTimeout(() => {
    front.remove();
    back.remove();
  }, 3000); // safety net
};

/* ---------- Component ---------- */

export const SplashPage: React.FC = () => {
  const navigate = useNavigate();
  const [count, setCount] = useState(0);
  const [visible, setVisible] = useState(false);
  const [exiting, setExiting] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [fadeOut, setFadeOut] = useState(false);

  const audioRef = useRef<AudioContext | null>(null);
  const noiseRef = useRef<AudioBuffer | null>(null);
  const samplesRef = useRef<AudioBuffer[]>([]);

  const reduced =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Keep html/body cream while the splash is on screen
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prevHtml = html.style.background;
    const prevBody = body.style.background;
    html.style.background = COLORS.bg;
    body.style.background = COLORS.bg;
    return () => {
      html.style.background = prevHtml;
      body.style.background = prevBody;
    };
  }, []);

  // Audio setup
  useEffect(() => {
    if (reduced) return;
    const Ctor: typeof AudioContext | undefined =
      window.AudioContext || (window as any).webkitAudioContext;
    if (!Ctor) return;

    let ctx: AudioContext;
    try {
      ctx = new Ctor();
    } catch {
      return;
    }
    audioRef.current = ctx;
    noiseRef.current = createNoise(ctx);
    ctx.resume().catch(() => undefined);

    TYPING_SOUNDS.forEach((url) => {
      fetch(url)
        .then((r) => (r.ok ? r.arrayBuffer() : Promise.reject()))
        .then((data) => ctx.decodeAudioData(data))
        .then((buf) => samplesRef.current.push(buf))
        .catch(() => undefined);
    });

    const unlock = () => {
      ctx.resume().catch(() => undefined);
    };
    const events = ['pointerdown', 'touchstart', 'keydown'] as const;
    events.forEach((e) => window.addEventListener(e, unlock, { passive: true }));

    return () => {
      events.forEach((e) => window.removeEventListener(e, unlock));
      ctx.close().catch(() => undefined);
      audioRef.current = null;
      samplesRef.current = [];
    };
  }, [reduced]);

  // Sequence
  useEffect(() => {
    let cancelled = false;
    const timers = new Set<number>();
    const sleep = (ms: number) =>
      new Promise<void>((resolve) => {
        const id = window.setTimeout(() => {
          timers.delete(id);
          resolve();
        }, ms);
        timers.add(id);
      });

    const run = async () => {
      await sleep(1200);
      if (cancelled) return;
      setVisible(true);

      if (reduced) {
        setCount(FULL.length);
        await sleep(2600);
        if (cancelled) return;
        setVisible(false);
        await sleep(700);
        if (cancelled) return;
        setFadeOut(true);
        await sleep(600);
        if (!cancelled) navigate('/login', { replace: true });
        return;
      }

      // Type: "The journey of Being that Engineer," ... pause ... "start here."
      for (let c = 1; c <= FULL.length; c += 1) {
        if (cancelled) return;
        const ch = FULL[c - 1];
        setCount(c);

        const ctx = audioRef.current;
        if (ctx && ctx.state === 'running' && ch !== ' ') {
          const samples = samplesRef.current;
          if (samples.length) {
            playSample(ctx, samples[Math.floor(Math.random() * samples.length)]);
          } else if (noiseRef.current) {
            playSynth(ctx, noiseRef.current);
          }
        }

        let delay = 95 + Math.random() * 55;
        if (ch === ' ') delay += 25;
        if (c === LINE_1.length) delay = 1250; // the beat before "start here."
        await sleep(delay);
      }

      await sleep(1900); // let it be read
      if (cancelled) return;

      // Exit: text lifts away, logo launches, curtain sweeps up
      setExiting(true);
      setVisible(false);
      await sleep(450);
      if (cancelled) return;
      setLaunching(true);
      await sleep(950);
      if (cancelled) return;
      runCurtain();
      navigate('/login', { replace: true });
    };

    run();
    return () => {
      cancelled = true;
      timers.forEach((id) => window.clearTimeout(id));
    };
  }, [navigate, reduced]);

  const n1 = Math.min(count, LINE_1.length);
  const n2 = Math.max(0, count - LINE_1.length);
  const caretOnFirst = n2 === 0;

  const caret = (
    <span
      className="sp-anim"
      style={{
        display: 'inline-block',
        width: 2,
        height: '0.85em',
        marginLeft: 2,
        marginRight: -4,
        verticalAlign: '-0.08em',
        background: COLORS.accent,
        animation: 'sp-caret 1s steps(1) infinite',
      }}
    />
  );

  return (
    <div
      role="status"
      aria-label={`${LINE_1} ${LINE_2}`}
      className="fixed inset-0 z-[80] overflow-hidden"
      style={{
        background: COLORS.bg,
        height: '100dvh',
        minHeight: '100vh',
        opacity: fadeOut ? 0 : 1,
        transition: 'opacity 600ms ease',
      }}
    >
      <style>{`
        @keyframes sp-ring { 0% { transform: scale(.85); opacity: .38; } 100% { transform: scale(2.1); opacity: 0; } }
        @keyframes sp-caret { 0%, 49% { opacity: 1; } 50%, 100% { opacity: 0; } }
        @keyframes sp-rise { from { opacity: 0; transform: translateY(14px) scale(.96); } to { opacity: 1; transform: none; } }
        @keyframes sp-launch {
          0%   { transform: scale(1); }
          16%  { transform: scale(.88); animation-timing-function: cubic-bezier(.65,0,.9,.3); }
          100% { transform: scale(34); }
        }
        @keyframes sp-img-out { 0%, 55% { opacity: 1; } 85%, 100% { opacity: 0; } }
        @keyframes sp-burst { 0% { transform: scale(.9); opacity: .55; } 100% { transform: scale(3.6); opacity: 0; } }
        .sp-text { top: calc(50% + 9.5rem); }
        @media (min-width: 768px) { .sp-text { top: calc(50% + 11rem); } }
        @media (prefers-reduced-motion: reduce) { .sp-anim { animation: none !important; } }
      `}</style>

      {/* Logo, exactly centred */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div
          className="sp-anim relative flex h-36 w-36 items-center justify-center md:h-44 md:w-44"
          style={{ animation: 'sp-rise 1.1s cubic-bezier(.2,.7,.2,1) both' }}
        >
          {[0, 1.6, 3.2].map((delay) => (
            <span
              key={delay}
              className="sp-anim absolute inset-0 rounded-full"
              style={{
                border: `1px solid ${COLORS.accent}`,
                animation: `sp-ring 4.8s ease-out ${delay}s infinite`,
                opacity: launching ? 0 : undefined,
                transition: 'opacity 250ms ease',
              }}
            />
          ))}

          {/* Burst ring when the logo launches */}
          <span
            className="sp-anim absolute inset-0 rounded-full"
            style={{
              border: `2px solid ${COLORS.accent}`,
              opacity: launching ? undefined : 0,
              animation: launching ? 'sp-burst 900ms ease-out 140ms both' : 'none',
            }}
          />

          {/* Orange card; the logo fills it edge to edge */}
          <div
            className="sp-anim relative h-28 w-28 overflow-hidden rounded-[30px] md:h-32 md:w-32"
            style={{
              background: COLORS.logoGradient,
              boxShadow:
                '0 22px 55px -16px rgba(255,111,15,0.65), 0 0 0 1px rgba(255,255,255,0.25) inset',
              animation: launching ? 'sp-launch 1000ms forwards' : undefined,
              willChange: 'transform',
            }}
          >
            <img
              src={LOGO_SRC}
              alt="MAIPT logo"
              className="sp-anim h-full w-full object-cover"
              style={{
                transform: `scale(${LOGO_ZOOM})`,
                animation: launching ? 'sp-img-out 1000ms forwards' : undefined,
              }}
            />
          </div>
        </div>
      </div>

      {/* Typed text, below the logo */}
      <div className="sp-text absolute left-0 right-0 flex justify-center px-6 text-center">
        <p
          className="max-w-3xl text-balance text-[1.6rem] italic leading-snug tracking-wide md:text-5xl"
          style={{
            fontFamily: "'Cormorant Garamond', Georgia, 'Times New Roman', serif",
            fontWeight: 500,
            color: COLORS.text,
            opacity: visible ? 1 : 0,
            filter: visible ? 'blur(0px)' : 'blur(6px)',
            transform: exiting ? 'translateY(-34px)' : 'translateY(0)',
            transition:
              'opacity 600ms ease, filter 600ms ease, transform 700ms cubic-bezier(.5,0,.2,1)',
          }}
        >
          <span style={{ display: 'block' }}>
            {LINE_1.slice(0, n1)}
            {caretOnFirst && caret}
            <span aria-hidden style={{ opacity: 0 }}>{LINE_1.slice(n1)}</span>
          </span>
          <span style={{ display: 'block', marginTop: '0.2em', fontSize: '1.12em' }}>
            {LINE_2.slice(0, n2)}
            {!caretOnFirst && caret}
            <span aria-hidden style={{ opacity: 0 }}>{LINE_2.slice(n2)}</span>
          </span>
        </p>
      </div>
    </div>
  );
};

export default SplashPage;